import React, {
    useCallback, useContext, useEffect, useMemo, useRef, useState
} from 'react';
import _ from 'lodash';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { SgCardService } from 'sg-capability-card';
import { SgPaymentUtils, ADOBE_TOPICS, COMMON_TAGGING_PROPERTIES } from 'saga-modules';
import { TranslatorContext } from '@jutro/locale';
import { Link, Icon } from '@jutro/components';
import { publish } from '@jutro/events';
import CommonConstants from 'sg-common-constants';
import metadata from './SgNewCardDetails.metadata.json5';
import messages from './SgNewCardDetails.messages';

function SgNewCardDetails(props) {
    const {
        modelVM,
        bind,
        isEndorsement,
        isRenewal,
        setShowFailedDownloadMsg,
        paymentDetails,
        productCode,
        amount,
        isArrears,
        customerDetails,
        hideCPAQuestion
    } = props;

    const translator = useContext(TranslatorContext);
    const autoRenewalStatus = useRef(true);
    const [, forceRerender] = useState();
    const [isIframeReady, setIsIframeReady] = useState(false);
    const barclayCardData = useRef(undefined);
    const branchNumber = isRenewal ? modelVM.value.branchNumber : null;
    const [showCPAButtons, setShowCPAButtons] = useState(false);
    const [isIframeDataLoaded, setIsIFrameDataLoaded] = useState(undefined);
    const [isLoading, setIsLoading] = useState(true);

    const onIFrameLoad = useCallback(() => {
        if (isIframeDataLoaded) {
            setIsLoading(false);
            setIsIFrameDataLoaded(false);
        }
    }, [isIframeDataLoaded]);

    const iframeComponentContent = useCallback(() => {
        return (
            <iframe
                title="iframe"
                id="barclayCardIframe"
                className="sg-card-iframe"
                name="barclayCardIframe"
                onLoad={onIFrameLoad}
            />
        );
    }, [onIFrameLoad]);

    const loaderContent = useCallback(() => {
        return (
            isLoading ? <div id="wizardLoader" className="sgAnimatedLoader" /> : null
        );
    }, [isLoading]);
    const processBarclayCardResponse = useCallback((useCardForRenewal, responseSaved, transactionUuid, ipAddress, screenHeight, screenWidth) => {
        let error = {};
        const shouldUseCardForRenewal = hideCPAQuestion ? false : useCardForRenewal;
        if (responseSaved === true) {
            const retrieveBarclayCardResponse = modelVM
                ? SgCardService.retrieveCepBarclayCardResponse
                : SgCardService.retrieveBarclayCardResponseDirectPayment;
            const barclayCardResponseCallParams = modelVM
                ? [modelVM.value.policyNumber, modelVM.value.jobID, transactionUuid, branchNumber, shouldUseCardForRenewal,
                    ipAddress, screenHeight, screenWidth]
                : [paymentDetails.displayName, transactionUuid, paymentDetails.balance.amount.toFixed(2), shouldUseCardForRenewal,
                    ipAddress, screenHeight, screenWidth];
            retrieveBarclayCardResponse(...barclayCardResponseCallParams)
                .then((result) => {
                    if (result === CommonConstants.PAYMENT_RESPONSES.SUCCESS
                        || (result.responseCode && result.responseCode === CommonConstants.PAYMENT_RESPONSES.SUCCESS)) {
                        bind(result);
                    } else {
                        error = { error: result };
                        throw error;
                    }
                })
                .catch((err) => {
                    error = { error: err, customErrorMessage: 'payment error' };
                    throw error;
                });
        }
    }, [bind, branchNumber, modelVM, paymentDetails, hideCPAQuestion]);

    const getBarclayCardResponse = useCallback((event) => {
        const responseProps = {
            autoRenewal: autoRenewalStatus.current !== undefined ? autoRenewalStatus.current : false,
            processBarclayCardResponse,
            event,
            barclayCardData: barclayCardData.current
        };
        SgPaymentUtils.handleBarclayCardResponse(responseProps);
    }, [autoRenewalStatus, processBarclayCardResponse]);

    const buildCustomerData = () => {
        return SgPaymentUtils.buildCustomerDataForIframe(modelVM.baseData.policyAddress, modelVM.baseData.accountEmail,
            modelVM.baseData.accountFirstName, modelVM.baseData.accountLastName);
    };

    const createBarclayCardRequest = useCallback(() => {
        const getCardDetails = modelVM ? SgCardService.getCepBarclayCardDetails : SgCardService.getBarclayCardDetailsDirectPayment;
        const customerData = modelVM ? buildCustomerData() : customerDetails;
        const cardDetailsCallParams = modelVM
            ? [modelVM.value.policyNumber, modelVM.value.jobID, branchNumber, autoRenewalStatus.current, window.innerHeight, window.innerWidth]
            : [paymentDetails.displayName, amount, autoRenewalStatus.current, window.innerHeight, window.innerWidth];
        getCardDetails(...cardDetailsCallParams)
            .then((result) => {
                barclayCardData.current = result;
                if (barclayCardData.current.hasExistingResponseForJobNumber && barclayCardData.current.hasExistingResponseForJobNumber === true) {
                    setShowFailedDownloadMsg(true);
                    processBarclayCardResponse(barclayCardData.current.hasExistingCpaActive, true, barclayCardData.current.transactionUid,
                        ...SgPaymentUtils.customerDeviceDetails(barclayCardData.current));
                } else {
                    SgPaymentUtils.barclayCardIframe(barclayCardData.current, customerData, isArrears, setIsIFrameDataLoaded);
                    setIsIframeReady(true);
                }
            })
            .catch((err) => {
                const error = { error: err, customErrorMessage: 'Not able to load Barclay metadata' };
                throw error;
            });
    }, [amount, branchNumber, isArrears, modelVM, paymentDetails, processBarclayCardResponse, setShowFailedDownloadMsg]);

    const handleAutoRenewal = useCallback((value) => {
        autoRenewalStatus.current = value;
        forceRerender({});
        const iFrameElement = document.getElementById('paymentConfirmation');
        if (!_.isNil(iFrameElement)) {
            iFrameElement.remove();
        }
        createBarclayCardRequest();
    }, [createBarclayCardRequest]);

    useEffect(() => {
        createBarclayCardRequest();
        publish(ADOBE_TOPICS.ADOBE_EVENT, { adobeID: COMMON_TAGGING_PROPERTIES.paymentAutoRenewal, value: true });
    }, []);

    useEffect(() => {
        window.addEventListener('message', getBarclayCardResponse);
        return () => {
            window.removeEventListener('message', getBarclayCardResponse);
            const iFrameElement = document.getElementById('paymentConfirmation');
            if (!_.isNil(iFrameElement)) {
                iFrameElement.remove();
            }
        };
    }, []);

    const avoidDrivingUninsuredBannerContent = useMemo(() => {
        return (
            <div className={isEndorsement || isRenewal ? 'sgAvoidDrivingUninsuredBanner' : 'sgAvoidDrivingUninsuredBannerPaymentDetails'}>
                <div className="sgAvoidDrivingUninsuredBannerContent">
                    <div className="sgAvoidDrivingUninsuredHeader">
                        <Icon icon="mi-info" className="sgAvoidDrivingUninsuredHeaderIcon" />
                        <h2 className="sgAvoidDrivingUninsuredHeaderText">{translator(messages.AvoidDrivingUninsuredHeader)}</h2>
                    </div>
                    <div className="sgAvoidDrivingUninsuredSubText">
                        <p>
                            <span>{translator(messages.AvoidDrivingUninsuredSubText)}</span>
                        </p>
                    </div>
                </div>
            </div>
        );
    }, [translator]);

    const continuousPaymentAuthorityText4Content = useMemo(() => {
        return (
            <>
                {translator(messages.RenewAutomaticallySubText4)}
                &nbsp;
                <Link
                    href="/"
                    id="insuranceDeclinedMoreHelpText"
                    onClick={(event) => {
                        event.preventDefault();
                        setShowCPAButtons(true);
                    }}
                >
                    {translator(messages.RenewAutomaticallySubText5)}
                </Link>
                &nbsp;
                {translator(messages.RenewAutomaticallySubText6)}
            </>
        );
    }, [translator]);

    const showAvoidDrivingUninsuredBanner = useMemo(() => {
        return !autoRenewalStatus.current
            && (productCode === CommonConstants.MOTOR_PRODUCT_CODE || productCode === CommonConstants.MOTORHOME_PRODUCT_CODE);
    }, [productCode, autoRenewalStatus.current]);

    const overrideProps = {
        iframeContainer: {
            content: iframeComponentContent()
        },
        continuousPaymentAuthoritySection: {
            visible: isIframeReady && !hideCPAQuestion ? '' : false
        },
        loaderContainer: {
            content: loaderContent()
        },
        paymentAutoRenewal: {
            value: autoRenewalStatus.current
        },
        continuousPaymentAuthorityText4: {
            content: continuousPaymentAuthorityText4Content
        },
        continuousPaymentAuthorityButtonsSection: {
            visible: showCPAButtons
        },
        avoidDrivingUninsuredBanner: {
            content: avoidDrivingUninsuredBannerContent,
            className: 'sgAvoidDrivingUninsuredContainer',
            visible: showAvoidDrivingUninsuredBanner
        },
        continuousPaymentAuthorityQuestion: {
            className: isEndorsement || isRenewal ? 'sgCpaQuestion' : 'sgCpaQuestionPaymentDetails'
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            handleAutoRenewal
        }
    };
    return renderContentFromMetadata(metadata.componentContent, overrideProps, resolvers);
}

export default SgNewCardDetails;
